/*
 * ============================================================
 * CCS BUILDER STATE — assets/ccs-builder-state.js
 * ============================================================
 * The design state behind the CCS shirt builder. No DOM, no canvas, no
 * network — so the rules that are expensive to get wrong can be tested
 * directly.
 *
 * ── How this differs from the SIB builder ──────────────────────────────────
 * SIB has ONE design and swaps the garment underneath it: picking a new color
 * replaces the shirt photo and the artwork stays put. That is right when you
 * are buying shirts — the same design, on a different color.
 *
 * CCS is the opposite. A seller is publishing a product, and artwork that
 * reads well on black often has to change on white — an inverted file, a
 * different ink color, sometimes a different image. So every color owns its
 * own design, and adding a color FORKS the current one rather than sharing
 * it: a copy to start from, independent the moment it exists.
 *
 * That fork is the single most important behaviour in this file. Sharing by
 * reference would mean editing white silently rewrote black, and the seller
 * would not find out until the product was live.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.CCSBuilderState = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var SIDES = ['front', 'back'];

  // Mirrors ccsApi.js. Enforced here too so the seller is stopped while
  // choosing rather than when saving a finished design.
  //
  // MAX_VARIANTS is the platform's cap on one product; MAX_COLORS is ours, and
  // it is the one that binds -- publishing runs an export-and-upload pass per
  // color, so colors are what a seller waits on. See the note in ccsApi.js.
  var MAX_COLORS = 25;
  var MAX_VARIANTS = 2048;

  function deepClone(value) {
    if (value === null || value === undefined) return value;
    // structuredClone where available; the JSON round trip is a fine fallback
    // because a fabric scene is already plain JSON by construction.
    if (typeof structuredClone === 'function') return structuredClone(value);
    return JSON.parse(JSON.stringify(value));
  }

  function emptySide() {
    return { scene: null, art: null, preview: null, dirty: false };
  }

  function emptyDesign(colorKey) {
    return { colorKey: colorKey, front: emptySide(), back: emptySide(), sizes: null };
  }

  function create(style, options) {
    var opts = options || {};
    if (!style || !Array.isArray(style.colors) || !style.colors.length) {
      throw new Error('A style with at least one color is required.');
    }

    var byKey = {};
    style.colors.forEach(function (color) { byKey[color.key] = color; });

    var state = {
      style: style,
      colorsByKey: byKey,
      designs: {},
      // Explicit rather than derived from Object.keys: this is the order
      // swatches appear in on the product, and object key order is not a
      // contract worth relying on.
      order: [],
      active: { colorKey: null, side: 'front' },
      /*
       * Which color the product page opens on.
       *
       * Held here rather than as a flag on each design, because exactly one
       * color has it and two rows both claiming it is a state this cannot
       * reach. The server validates it against the colors actually sent, and
       * the product page reads it from the listing metafield -- so this is the
       * only place a seller's choice has to survive.
       */
      defaultColorKey: null,
      /*
       * An optional photo that stands in for the listing in collections, and
       * only there -- the product page shows the front and back of whichever
       * color the shopper picked, which is what they are buying.
       *
       * null means "as before": the default color's front. Everything about
       * this is additive, so a listing that never sets one behaves exactly as
       * it does today.
       */
      featureImageUrl: null,
      markupCents: opts.markupCents == null ? 300 : opts.markupCents
    };

    // Open on the style's default color, or the first that exists.
    var first = (byKey[style.defaultColor] && style.defaultColor) || style.colors[0].key;
    addColor(state, first, { fork: false });
    state.active.colorKey = first;
    // A listing always has a default; until the seller says otherwise it is
    // the one they started on.
    state.defaultColorKey = first;

    return state;
  }

  // ── Colors ───────────────────────────────────────────────────────────────

  /*
   * Add a color, starting from a copy of the design currently on screen.
   *
   * `fork` defaults to true and is what makes adding a second color feel like
   * continuing rather than starting again. The copy is deep: the two designs
   * share nothing once this returns.
   */
  function addColor(state, colorKey, options) {
    var opts = options || {};
    var color = state.colorsByKey[colorKey];
    if (!color) return { error: 'That color is not available on this garment.' };
    if (state.designs[colorKey]) return { error: color.name + ' is already in your design.' };
    if (state.order.length >= MAX_COLORS) {
      return { error: 'You can offer up to ' + MAX_COLORS + ' colors on one design.' };
    }

    var design = emptyDesign(colorKey);

    var source = opts.fork === false ? null : state.designs[state.active.colorKey];
    if (source) {
      SIDES.forEach(function (side) {
        if (!source[side].scene) return;
        design[side].scene = rebaseScene(
          deepClone(source[side].scene),
          state.colorsByKey[source.colorKey],
          color
        );
        // Forked artwork has not been exported yet — the scene is a copy, the
        // rendered files are not.
        design[side].dirty = true;
      });
    }

    // Every size the color comes in, until the seller says otherwise. Opting
    // out is the rarer intent, so it should be the one that takes a click.
    design.sizes = (color.sizes || [])
      .filter(function (s) { return s.gtin; })
      .map(function (s) { return s.name; });

    state.designs[colorKey] = design;
    state.order.push(colorKey);
    return { design: design };
  }

  /*
   * Put the colors in the order the seller dragged them into.
   *
   * This is the order shoppers see: it becomes the Color option's value order
   * on the product, so the swatch a seller wants first is first. Before this
   * it was fixed at the order they happened to add them in, which is the order
   * they were thinking about colors, not the order they want to sell them.
   *
   * Anything the caller leaves out keeps its place at the end rather than
   * being dropped -- a reorder is a reorder, never a removal, and a stale list
   * from a browser must not be able to make a color disappear.
   */
  function setColorOrder(state, keys) {
    var seen = {};
    var next = [];
    (keys || []).forEach(function (key) {
      if (!state.designs[key] || seen[key]) return;
      seen[key] = true;
      next.push(key);
    });
    state.order.forEach(function (key) {
      if (seen[key]) return;
      seen[key] = true;
      next.push(key);
    });
    state.order = next;
    return { order: state.order.slice() };
  }

  /*
   * Nudge one color up or down. What the keyboard uses, and what drag falls
   * back to -- dragging is unusable without a pointer.
   */
  function moveColor(state, colorKey, delta) {
    var at = state.order.indexOf(colorKey);
    var to = at + delta;
    if (at === -1 || to < 0 || to >= state.order.length) return { order: state.order.slice() };
    var next = state.order.slice();
    next.splice(to, 0, next.splice(at, 1)[0]);
    state.order = next;
    return { order: state.order.slice() };
  }

  function removeColor(state, colorKey) {
    if (!state.designs[colorKey]) return { error: 'That color is not in your design.' };
    if (state.order.length === 1) {
      return { error: 'A design needs at least one color.' };
    }

    // Where it sat, captured before it goes: landing anywhere else means the
    // seller loses their place in the list.
    var wasAt = state.order.indexOf(colorKey);

    delete state.designs[colorKey];
    state.order = state.order.filter(function (key) { return key !== colorKey; });

    /*
     * Removing what you were looking at has to land somewhere, or the canvas
     * is left pointing at a design that no longer exists.
     *
     * The one BEFORE it, not the first. Deleting the eighth of ten colors and
     * being dropped back to the first means finding your way back with Next,
     * seven times. Deleting the first lands on the new first, since there is
     * nothing before it.
     */
    if (state.active.colorKey === colorKey) {
      state.active.colorKey = state.order[Math.max(0, wasAt - 1)];
    }
    // Same for the default: a listing without one has no color to open on,
    // and the first remaining is what the server would have picked anyway.
    if (state.defaultColorKey === colorKey) {
      state.defaultColorKey = state.order[0];
    }
    return { removed: colorKey, active: state.active.colorKey };
  }

  /*
   * Object positions live in design units against a canvas whose height comes
   * from the garment photo's aspect ratio. A color shot at a different ratio
   * would otherwise shift the artwork up or down the shirt, so vertical
   * positions are scaled by the ratio change — the design stays where it
   * looked, rather than where its numbers said.
   *
   * Mirrors the rebasing in the SIB builder's pickColor().
   */
  function rebaseScene(scene, fromColor, toColor) {
    var from = aspect(fromColor);
    var to = aspect(toColor);
    if (!from || !to || from === to) return scene;

    var k = from / to;
    (scene.objects || []).forEach(function (object) {
      if (typeof object.top === 'number') object.top = object.top * k;
    });
    return scene;
  }

  function aspect(color) {
    if (!color || !color.natural) return null;
    var w = Number(color.natural.w);
    var h = Number(color.natural.h);
    return (w > 0 && h > 0) ? (h / w) : null;
  }

  /*
   * Which color the product opens on.
   *
   * Only a color that is in the design can be it. There is no way to clear it:
   * something has to be shown first, so "none" is not a state a listing can be
   * in -- the seller changes it by choosing another, not by unchoosing this.
   */
  function setDefaultColor(state, colorKey) {
    if (!state.designs[colorKey]) {
      return { error: 'That color is not in your design.' };
    }
    state.defaultColorKey = colorKey;
    return { defaultColorKey: colorKey };
  }

  // The default, guaranteed to name a color that is actually in the design.
  function defaultColor(state) {
    return state.designs[state.defaultColorKey] ? state.defaultColorKey : state.order[0];
  }

  function setFeatureImage(state, url) {
    state.featureImageUrl = url || null;
    return { featureImageUrl: state.featureImageUrl };
  }

  // ── Sizes ─────────────────────────────────────────────────────────────────

  function setSizes(state, colorKey, sizeNames) {
    var design = state.designs[colorKey];
    if (!design) return { error: 'That color is not in your design.' };

    var available = {};
    (state.colorsByKey[colorKey].sizes || []).forEach(function (size) {
      if (size.gtin) available[String(size.name).toUpperCase()] = size.name;
    });

    var chosen = [];
    (sizeNames || []).forEach(function (name) {
      var match = available[String(name).toUpperCase()];
      if (match && chosen.indexOf(match) === -1) chosen.push(match);
    });

    if (!chosen.length) {
      return { error: 'Keep at least one size for ' + state.colorsByKey[colorKey].name + '.' };
    }
    design.sizes = chosen;
    return { sizes: chosen };
  }

  function variantCount(state) {
    return state.order.reduce(function (total, key) {
      return total + (state.designs[key].sizes || []).length;
    }, 0);
  }

  // ── Scenes ────────────────────────────────────────────────────────────────

  function setScene(state, colorKey, side, scene) {
    var design = state.designs[colorKey];
    if (!design || SIDES.indexOf(side) === -1) return null;
    design[side].scene = scene;
    // Any edit invalidates whatever was exported before it.
    design[side].dirty = true;
    design[side].art = null;
    design[side].preview = null;
    return design[side];
  }

  function setRendered(state, colorKey, side, rendered) {
    var design = state.designs[colorKey];
    if (!design || SIDES.indexOf(side) === -1) return null;
    design[side].art = rendered.art || null;
    design[side].preview = rendered.preview || null;
    design[side].sceneUrl = rendered.sceneUrl || null;
    design[side].dirty = false;
    return design[side];
  }

  function hasArtwork(side) {
    return !!(side && side.scene && Array.isArray(side.scene.objects) && side.scene.objects.length);
  }

  function sidesNeedingExport(state) {
    var pending = [];
    state.order.forEach(function (colorKey) {
      var design = state.designs[colorKey];
      SIDES.forEach(function (side) {
        if (hasArtwork(design[side]) && design[side].dirty) {
          pending.push({ colorKey: colorKey, side: side });
        }
      });
    });
    return pending;
  }

  // ── Validation ────────────────────────────────────────────────────────────

  /*
   * Can this be published?
   *
   * Every chosen color must carry a design. A color with none would publish
   * a swatch that shows a blank shirt — which looks like a bug to a shopper
   * and is invisible to the seller until it is live.
   */
  function validate(state) {
    var errors = [];

    if (!state.order.length) errors.push('Pick at least one color.');

    state.order.forEach(function (colorKey) {
      var design = state.designs[colorKey];
      var name = state.colorsByKey[colorKey].name;

      if (!hasArtwork(design.front) && !hasArtwork(design.back)) {
        errors.push(name + ' has no design on it yet.');
      }
      if (!(design.sizes || []).length) {
        errors.push('Pick at least one size for ' + name + '.');
      }
    });

    var variants = variantCount(state);
    if (variants > MAX_VARIANTS) {
      errors.push('That is ' + variants + ' color and size combinations; the most one design ' +
        'can offer is ' + MAX_VARIANTS + '. Remove a color or a few sizes.');
    }

    return { valid: errors.length === 0, errors: errors };
  }

  // ── Output ────────────────────────────────────────────────────────────────

  /*
   * The POST /apps/ccs/listing body.
   *
   * Only what the server cannot work out for itself: which style, which
   * colors, which sizes, where the uploaded files are, and the markup. Prices,
   * GTINs, blank costs and garment art are all resolved server-side from the
   * SIB feed — sending them from here would mean sending numbers the browser
   * could have altered.
   */
  function toListingPayload(state, meta) {
    var info = meta || {};
    return {
      name: info.name,
      description: info.description,
      markup: state.markupCents / 100,
      sourceStyleHandle: state.style.handle,
      defaultColorKey: defaultColor(state),
      // Omitted rather than sent as null when unset, so the server can tell
      // "leave it alone" from "clear it" on an edit.
      featureImage: state.featureImageUrl || '',
      colors: state.order.map(function (colorKey) {
        var design = state.designs[colorKey];
        var payload = { key: colorKey, sizes: design.sizes || [] };
        SIDES.forEach(function (side) {
          if (!hasArtwork(design[side])) return;
          payload[side] = {
            art: design[side].art,
            scene: design[side].sceneUrl,
            preview: design[side].preview
          };
        });
        return payload;
      })
    };
  }

  /*
   * What the pricing step asks the server to quote. Same shape the listing
   * payload uses for colors, so the number shown and the number charged come
   * from the same description of the design.
   */
  function toQuotePayload(state) {
    return {
      sourceStyleHandle: state.style.handle,
      markup: state.markupCents / 100,
      colors: state.order.map(function (colorKey) {
        return { key: colorKey, sizes: state.designs[colorKey].sizes || [] };
      })
    };
  }

  return {
    SIDES: SIDES,
    MAX_COLORS: MAX_COLORS,
    MAX_VARIANTS: MAX_VARIANTS,
    create: create,
    addColor: addColor,
    removeColor: removeColor,
    setSizes: setSizes,
    setDefaultColor: setDefaultColor,
    setFeatureImage: setFeatureImage,
    setColorOrder: setColorOrder,
    moveColor: moveColor,
    defaultColor: defaultColor,
    setScene: setScene,
    setRendered: setRendered,
    hasArtwork: hasArtwork,
    sidesNeedingExport: sidesNeedingExport,
    variantCount: variantCount,
    validate: validate,
    rebaseScene: rebaseScene,
    toListingPayload: toListingPayload,
    toQuotePayload: toQuotePayload
  };
});
